/**
 * Relevé des factures ouvertes d'un revendeur, moins les avoirs disponibles.
 * Même charte que le relevé de compte (ResellerStatementPdf).
 */
import React from "react";
import { Document, Page, View, Text, Image } from "@react-pdf/renderer";
import { COMPANY_HEADER } from "../lib/company";
import { styles, fmtCHF, fmtDate, fmtDateFull } from "./ResellerStatementPdf";

const col = {
  ref: { width: "18%" }, date: { width: "13%" }, due: { width: "13%" }, late: { width: "12%", textAlign: "right" },
  total: { width: "14%", textAlign: "right" }, paid: { width: "14%", textAlign: "right" }, rest: { flex: 1, textAlign: "right" },
};

function Sender({ settings, logoDataUrl }) {
  const lines = (settings?.company_address || "").split("\n").filter(Boolean);
  const logo = logoDataUrl || settings?.company_logo_url;
  return (
    <View style={styles.senderBlock}>
      {logo && <Image src={logo} style={styles.senderLogo} />}
      <View style={styles.senderInfo}>
        <Text style={styles.senderName}>{COMPANY_HEADER}</Text>
        {lines.map((l, i) => <Text key={i} style={styles.senderMeta}>{l}</Text>)}
        {(settings?.company_postal_code || settings?.company_city) && (
          <Text style={styles.senderMeta}>CH-{settings?.company_postal_code || ""} {settings?.company_city || ""}</Text>
        )}
        {settings?.company_phone && <Text style={styles.senderMeta}>Tél {settings.company_phone}</Text>}
        {settings?.company_email && <Text style={styles.senderMeta}>{settings.company_email}</Text>}
        {settings?.company_vat_number && <Text style={styles.senderMeta}>TVA : {settings.company_vat_number}</Text>}
      </View>
    </View>
  );
}

function InvoiceRows({ invoices }) {
  return (
    <View style={styles.table}>
      <View style={styles.th}>
        <Text style={[styles.thCell, col.ref]}>Facture</Text>
        <Text style={[styles.thCell, col.date]}>Date</Text>
        <Text style={[styles.thCell, col.due]}>Échéance</Text>
        <Text style={[styles.thCell, col.late]}>Retard</Text>
        <Text style={[styles.thCell, col.total]}>Montant</Text>
        <Text style={[styles.thCell, col.paid]}>Déjà payé</Text>
        <Text style={[styles.thCell, col.rest]}>Reste dû</Text>
      </View>
      {invoices.length === 0 && (
        <View style={styles.tr}><Text style={{ ...styles.td, textAlign: "center", flex: 1, color: "#A3A6A3" }}>Aucune facture ouverte</Text></View>
      )}
      {invoices.map((i) => (
        <View key={i.id} style={styles.tr}>
          <Text style={[styles.td, col.ref, { fontFamily: "Helvetica-Bold" }]}>{i.reference}</Text>
          <Text style={[styles.td, col.date]}>{fmtDate(i.issue_date)}</Text>
          <Text style={[styles.td, col.due]}>{fmtDate(i.due_date)}</Text>
          <Text style={[styles.td, col.late, i.days_overdue > 0 ? { color: "#B24A32" } : {}]}>{i.days_overdue > 0 ? `${i.days_overdue} j` : "—"}</Text>
          <Text style={[styles.td, col.total]}>{fmtCHF(i.total_ttc)}</Text>
          <Text style={[styles.td, col.paid]}>{Number(i.paid_amount) > 0 ? fmtCHF(i.paid_amount) : "—"}</Text>
          <Text style={[styles.td, col.rest, { fontFamily: "Helvetica-Bold" }]}>{fmtCHF(i.amount_due)}</Text>
        </View>
      ))}
    </View>
  );
}

function CreditRows({ credits }) {
  if (!credits.length) return null;
  return (
    <View style={[styles.table, { marginTop: 10 }]}>
      <View style={styles.th}>
        <Text style={[styles.thCell, col.ref]}>Avoir</Text>
        <Text style={[styles.thCell, col.date]}>Date</Text>
        <Text style={[styles.thCell, { flex: 1 }]}>Motif</Text>
        <Text style={[styles.thCell, col.total]}>Montant</Text>
        <Text style={[styles.thCell, col.rest]}>Disponible</Text>
      </View>
      {credits.map((c) => (
        <View key={c.id} style={styles.tr}>
          <Text style={[styles.td, col.ref, { fontFamily: "Helvetica-Bold" }]}>{c.reference}</Text>
          <Text style={[styles.td, col.date]}>{fmtDate(c.issue_date)}</Text>
          <Text style={[styles.td, { flex: 1 }]}>{c.reason_label}</Text>
          <Text style={[styles.td, col.total]}>{fmtCHF(c.total_ttc)}</Text>
          <Text style={[styles.td, col.rest, { color: "#3E5C34", fontFamily: "Helvetica-Bold" }]}>- {fmtCHF(c.remaining)}</Text>
        </View>
      ))}
    </View>
  );
}

function Totals({ totals }) {
  const net = totals.net_due;
  const row = (label, value, extra = {}) => (
    <View style={styles.summaryRow}>
      <Text style={styles.summaryLabel}>{label}</Text>
      <Text style={[styles.summaryValue, extra]}>{value}</Text>
    </View>
  );
  return (
    <View style={styles.summary}>
      {row("Total des factures ouvertes", `${fmtCHF(totals.invoices_due)} CHF`)}
      {totals.credits_available > 0 && row("Avoirs disponibles", `- ${fmtCHF(totals.credits_available)} CHF`, { color: "#3E5C34" })}
      <View style={[styles.summaryRow, { borderTop: "1pt solid #1F4E3D", marginTop: 3, paddingTop: 4 }]}>
        <Text style={[styles.summaryValue, { fontSize: 10.5 }]}>{net >= 0 ? "Montant net à payer" : "Solde en votre faveur"}</Text>
        <Text style={[styles.summaryValue, { fontSize: 10.5, color: net > 0.01 ? "#B24A32" : "#3E5C34" }]}>{fmtCHF(Math.abs(net))} CHF</Text>
      </View>
      {totals.overdue > 0 && row("dont échu", `${fmtCHF(totals.overdue)} CHF`, { color: "#B24A32" })}
    </View>
  );
}

export default function ResellerOpenItemsPdf({ data, settings, logoDataUrl }) {
  if (!data?.reseller) return null;
  const r = data.reseller;
  const debtorLines = [r.contact_name, r.address, `${r.postal_code || ""} ${r.city || ""}`.trim()].filter(Boolean);
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <Sender settings={settings} logoDataUrl={logoDataUrl} />
          <View style={styles.titleRight}>
            <Text style={styles.titleMain}>RELEVÉ</Text>
            <Text style={styles.titleSub}>Factures ouvertes</Text>
            <Text style={styles.titleRef}>État au {fmtDateFull(data.as_of)}</Text>
          </View>
        </View>
        <View style={styles.debtorWindow}>
          <Text style={styles.debtorName}>{r.name}</Text>
          {debtorLines.map((l, i) => <Text key={i} style={styles.debtorLine}>{l}</Text>)}
        </View>
        <View style={styles.postWindow} />
        <InvoiceRows invoices={data.invoices || []} />
        <CreditRows credits={data.credit_notes || []} />
        <Totals totals={data.totals || {}} />
        <View style={styles.legal}>
          <Text style={styles.legalBold}>Note :</Text>
          <Text>
            Ce relevé liste les factures non encore entièrement réglées à la date indiquée
            {data.credit_notes?.length ? ", diminuées des avoirs encore disponibles" : ""}.
            Si un paiement est déjà en route, merci de ne pas tenir compte de ce relevé. Pour toute question, contactez-nous à l&apos;adresse indiquée en en-tête.
          </Text>
        </View>
        <Text style={styles.footer}>Relevé des factures ouvertes {r.name} · État au {fmtDate(data.as_of)}</Text>
      </Page>
    </Document>
  );
}
